import axios from 'axios';

// BASE_URL is set by Vite from the --base CLI flag (e.g. '/apps/cosware/' in subpath deploys, '/' in dev).
// It always ends with '/', so appending 'api' gives the correct API prefix without double slashes.
const BASE_PATH = import.meta.env.BASE_URL || '/';
const API_URL = `${BASE_PATH}api`;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Session marker: AuthContext writes a new value here on every change of signed-in
// account (sign-in, « agir en tant que » start and exit, group switch, sign-out),
// never on a token refresh. Shared by all tabs, like the tokens.
const SESSION_KEY = 'authSession';
const currentSession = () => localStorage.getItem(SESSION_KEY);

// Request interceptor — attach JWT and remember which session sent the request.
// A replay after a refresh keeps both, so it can only ever run as the same account.
api.interceptors.request.use((config) => {
  if (config._session === undefined) {
    config._session = currentSession();
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

function forceLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem(SESSION_KEY);
  window.location.href = `${BASE_PATH}login`;
}

// Response interceptor — attempt refresh on 401, then redirect if that fails.
// 403 (permission denied) is NEVER retried or treated as an auth failure — during
// impersonation a 403 just means the endpoint returned forbidden, not that the token
// is invalid. Retrying / logging out on 403 would destroy the impersonation session.
// A 401 answered after the account changed belongs to the previous session: it is
// passed to the caller as is — no refresh (the stored refresh token is the new
// account's), no replay, no logout of the new account.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) return Promise.reject(error);
    const status = error.response?.status;
    const isAuthRoute = originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/login');
    const sessionChanged = () => originalRequest._session !== currentSession();

    if (!originalRequest._retry && !isAuthRoute) {
      if (status === 401) {
        if (sessionChanged()) return Promise.reject(error);
        originalRequest._retry = true;
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          try {
            const { data } = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
            // The account changed while refreshing: keep the new account's tokens
            if (sessionChanged()) return Promise.reject(error);
            localStorage.setItem('token', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);
            originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
            return api(originalRequest);
          } catch {
            // Refresh failed — fall through to logout
          }
        }
        if (sessionChanged()) return Promise.reject(error);
        forceLogout();
      }
      // 403: permission denied — pass through as a normal error, never logout or retry.
      // This is especially important during impersonation where a 403 from an
      // impersonation-unaware endpoint must not destroy the active session.
    } else if (originalRequest._retry && status === 401 && !sessionChanged()) {
      // Retry also failed with 401 — force logout
      forceLogout();
    }
    return Promise.reject(error);
  }
);

export default api;
